import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import {
  ArrowLeft,
  Send,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Upload,
  Link as LinkIcon,
  FileText,
  Calendar,
  Hash,
  AlignLeft,
  Mail,
  CheckSquare,
} from "lucide-react";
import { tr } from "../components/client-portal/i18n";

const FIELD_ICONS = {
  checkbox: CheckSquare,
  text: FileText,
  number: Hash,
  dropdown: FileText,
  file: Upload,
  date: Calendar,
  textarea: AlignLeft,
  url: LinkIcon,
  email: Mail,
};

export default function ClientRequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [responseComment, setResponseComment] = useState("");
  const [fieldResponses, setFieldResponses] = useState({});

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    try {
      const res = await api.get(`/client-requests/${id}`);
      setRequest(res.data);
      
      // Initialize field responses from template_fields
      const fields = res.data.template_fields || res.data.fields || [];
      if (fields.length > 0) {
        const initial = {};
        const templateData = res.data.template_data || {};
        fields.forEach(f => {
          // Use existing response or template_data value
          initial[f.id] = templateData[f.id] || f.response_value || "";
        });
        setFieldResponses(initial);
      }
    } catch (error) {
      console.error("Error fetching request:", error);
      toast.error(tr("Не вдалося завантажити запит"));
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      // Validate required fields
      const fields = request.template_fields || request.fields || [];
      const requiredFields = fields.filter(f => f.is_required);
      const missingFields = requiredFields.filter(f => !fieldResponses[f.id]);
      
      if (missingFields.length > 0) {
        toast.error(`${tr("Заповніть обовʼязкові поля")}: ${missingFields.map(f => f.label).join(", ")}`);
        setSubmitting(false);
        return;
      }
      
      // Prepare response data
      const responseData = {
        response_type: "info_provided",
        response_comment: responseComment,
        field_responses: Object.entries(fieldResponses).map(([fieldId, value]) => ({
          field_id: fieldId,
          value: value,
        })),
      };
      
      await api.put(`/client-requests/${id}/respond`, responseData);
      toast.success(tr("Відповідь надіслано"));
      navigate(-1);
    } catch (error) {
      console.error("Error submitting response:", error);
      toast.error(error.response?.data?.detail || tr("Не вдалося надіслати відповідь"));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("uk-UA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12 text-zinc-500">
        {tr("Запит не знайдено")}
      </div>
    );
  }

  const isResponded = request.status === "responded";
  const canRespond = (user?.role === "partner" || user?.role === "client") && !isResponded;
  const fields = request.template_fields || request.fields || [];
  const templateData = request.template_data || {};

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="client-request-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {tr("Назад")}
        </Button>
      </div>

      {/* Request Info Card */}
      <Card className="bg-zinc-900/50 border-zinc-800 rounded-2xl">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-zinc-50 text-lg font-['Manrope']">{request.title}</CardTitle>
              <p className="text-sm text-zinc-500 mt-1">
                {request.project_name || tr("Без проєкту")} • {request.request_type?.replace(/_/g, " ")}
              </p>
            </div>
            <Badge variant="outline" className={cn("text-xs",
              isResponded
                ? "bg-green-500/10 text-green-400 border-green-500/30"
                : request.is_overdue
                  ? "bg-red-500/10 text-red-400 border-red-500/30"
                  : "bg-orange-500/10 text-orange-400 border-orange-500/30"
            )}>
              {isResponded ? tr("Завершено") : request.is_overdue ? tr("ПРОСТРОЧЕНО") : tr("Очікує")}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {request.description && (
            <p className="text-zinc-400 text-sm mb-4">{request.description}</p>
          )}
          <div className="flex gap-4 text-xs text-zinc-500">
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {tr("Створено")}: {formatDate(request.created_at)}
            </div>
            <div className={cn("flex items-center gap-1", request.is_overdue && "text-red-400")}>
              {request.is_overdue && <AlertTriangle className="h-3.5 w-3.5" />}
              {tr("Термін")}: {formatDate(request.deadline)}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fields Section */}
      {fields.length > 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800 rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-zinc-50 text-base font-['Manrope']">
              {isResponded ? tr("Надіслана інформація") : tr("Заповніть, будь ласка")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {fields.map((field) => {
              const Icon = FIELD_ICONS[field.field_type] || FileText;
              const currentValue = isResponded ? (templateData[field.id] || "—") : (fieldResponses[field.id] || "");
              
              return (
                <div key={field.id} className="space-y-1.5">
                  <Label className="text-zinc-300 text-sm flex items-center gap-2">
                    <Icon className="h-4 w-4 text-zinc-500" />
                    {field.label}
                    {field.is_required && <span className="text-red-400">*</span>}
                  </Label>
                  
                  {isResponded ? (
                    // Show response value
                    <div className="p-3 bg-zinc-800/50 rounded-md text-zinc-50 text-sm min-h-[40px]">
                      {currentValue === "true" ? tr("Так") : currentValue === "false" ? tr("Ні") : currentValue || "—"}
                    </div>
                  ) : canRespond ? (
                    // Input fields for response
                    field.field_type === "textarea" ? (
                      <Textarea
                        value={fieldResponses[field.id] || ""}
                        onChange={(e) => setFieldResponses({...fieldResponses, [field.id]: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-zinc-50 text-sm"
                        placeholder={field.placeholder || `${tr("Введіть")}: ${field.label.toLowerCase()}...`}
                        rows={3}
                      />
                    ) : field.field_type === "checkbox" ? (
                      <div className="flex items-center gap-2 p-2">
                        <input
                          type="checkbox"
                          id={field.id}
                          checked={fieldResponses[field.id] === "true" || fieldResponses[field.id] === true}
                          onChange={(e) => setFieldResponses({...fieldResponses, [field.id]: e.target.checked ? "true" : "false"})}
                          className="h-4 w-4 rounded border-zinc-600 bg-zinc-800"
                        />
                        <label htmlFor={field.id} className="text-zinc-400 text-sm cursor-pointer">
                          {field.placeholder || tr("Так, підтверджую")}
                        </label>
                      </div>
                    ) : field.field_type === "file" || field.field_type === "url" ? (
                      <Input
                        type="url"
                        value={fieldResponses[field.id] || ""}
                        onChange={(e) => setFieldResponses({...fieldResponses, [field.id]: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-zinc-50 text-sm"
                        placeholder={field.placeholder || tr("Вставте посилання (Google Drive, Dropbox...)")}
                      />
                    ) : field.field_type === "dropdown" && field.options ? (
                      <select
                        value={fieldResponses[field.id] || ""}
                        onChange={(e) => setFieldResponses({...fieldResponses, [field.id]: e.target.value})}
                        className="w-full p-2 bg-zinc-800 border border-zinc-700 rounded-md text-zinc-50 text-sm"
                      >
                        <option value="">{tr("Оберіть...")}</option>
                        {field.options.map((opt, i) => (
                          <option key={i} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        type={field.field_type === "email" ? "email" : field.field_type === "number" ? "number" : field.field_type === "date" ? "date" : "text"}
                        value={fieldResponses[field.id] || ""}
                        onChange={(e) => setFieldResponses({...fieldResponses, [field.id]: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-zinc-50 text-sm"
                        placeholder={field.placeholder || `${tr("Введіть")}: ${field.label.toLowerCase()}...`}
                      />
                    )
                  ) : (
                    <div className="p-3 bg-zinc-800/50 rounded-md text-zinc-500 text-sm">
                      {tr("Очікує на відповідь")}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-zinc-900/50 border-zinc-800 rounded-2xl">
          <CardContent className="py-6 text-center text-zinc-500">
            {tr("Окремих полів не запитували")}
          </CardContent>
        </Card>
      )}

      {/* Comment & Submit Section */}
      {canRespond && (
        <Card className="bg-zinc-900/50 border-zinc-800 rounded-2xl">
          <CardContent className="pt-4 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-zinc-300 text-sm">{tr("Додаткові коментарі (необовʼязково)")}</Label>
              <Textarea
                value={responseComment}
                onChange={(e) => setResponseComment(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-50 text-sm"
                placeholder={tr("Будь-які додаткові примітки...")}
                rows={2}
              />
            </div>
            
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            >
              {submitting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {tr("Надіслати відповідь")}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Already Responded */}
      {isResponded && (
        <Card className="bg-green-500/5 border-green-500/20 rounded-2xl">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-400" />
              <div>
                <p className="text-green-400 font-medium text-sm">{tr("Відповідь надіслано")}</p>
                {request.response_comment && (
                  <p className="text-xs text-zinc-400 mt-1">"{request.response_comment}"</p>
                )}
                <p className="text-xs text-zinc-500 mt-1">{formatDate(request.responded_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
